"use client";

import { c as _c } from "react/compiler-runtime";
import { getServicePricingForSelfSchedule } from "../steps/services-step/components/helpers";
import { PromotionPriceDisplay } from "../steps/services-step/components/promotion-price-display";
import { PromotionBadge } from "../steps/services-step/components/service-menu-item/components";
import { BsFillQuestionCircleFill } from "react-icons/bs";
import { FaMapMarkerAlt, FaPhoneAlt, FaUser } from "react-icons/fa";
import Image from "next/image";
import { Button, Card, Tooltip } from "@/components/ui";
import { PopularBadge } from "@/components/ui/popular-badge";
import QuantityStepper from "@/components/ui/quantity-stepper";
import { TelehealthBadge } from "@/components/ui/telehealth-badge";
import { useCatalogImageLayout } from "@/contexts/catalog-image-layout-provider";
import { useIsRwgLanding } from "@/contexts/self-schedule-provider";
import { formatAddress, formatCurrency, formatDistance, formatStaffName } from "@/lib/format-data";
import { FeeStructure, computeBookingFeeCents } from "@/lib/service-fees";
import { patientFacingDurationLabel } from "@/lib/service-utils";
import { cx } from "@/lib/tailwind-utils";
import { LocationWithVisibility } from "@/types/location";
import { PublicProviderSummary } from "@/types/provider";
import { PublicServiceDetail } from "@/types/service";
type SelfScheduleCardItem = {
  id: string;
  name: string;
  subtitle?: string;
  caption?: string;
  description?: string;
  isPopular: boolean;
  /** A virtual service, by video wherever it's booked. */
  isTelehealth?: boolean;
  pricing?: FeeStructure;
  address?: React.ReactNode;
  image?: string | React.ElementType<any> | React.ReactNode;
  isPreviouslyAppointment?: boolean;
  type: "service" | "location" | "category" | "provider";
};
type SelfScheduleCardProps = {
  item: SelfScheduleCardItem;
  isSelected: boolean;
  /** Number of selected instances for this item. */
  selectionCount?: number;
  maxQuantity?: number;
  onQuantityChange?: (itemId: string, quantity: number) => void;
  isPreviousAppointment?: boolean;
  disabled?: boolean;
  onItemClick: (itemId: string) => void;
  onInfoClick?: (e: React.MouseEvent<HTMLElement | SVGElement>, itemId: string) => void;
  animationDelay?: number;
  className?: string;
  hideInfoButton?: boolean;
  /** Full-width content along the bottom edge, below everything else. */
  footer?: React.ReactNode;
};
const getTypeMessage = (type: "service" | "location" | "category" | "provider") => {
  switch (type) {
    case "service":
      return "You've booked this service before.";
    case "category":
      return "You've booked a service in this category before.";
    case "location":
      return "Your preferred location.";
    case "provider":
      return "Your preferred provider.";
  }
};
export const SelfScheduleCard = t0 => {
  const $ = _c(110);
  const {
    item,
    isSelected,
    selectionCount: t1,
    maxQuantity,
    onQuantityChange,
    isPreviousAppointment,
    disabled: t2,
    onItemClick,
    onInfoClick,
    animationDelay,
    className,
    hideInfoButton: t3,
    footer
  } = t0;
  const selectionCount = t1 === undefined ? 0 : t1;
  const disabled = t2 === undefined ? false : t2;
  const hideInfoButton = t3 === undefined ? false : t3;
  const showQuantityStepper = item.type === "service" && isSelected && selectionCount > 0;
  const isRwgLanding = useIsRwgLanding();
  const {
    catalogImageLayout
  } = useCatalogImageLayout();
  const hasServiceImage = item.type === "service" && typeof item.image === "string";
  const usesSquareServiceImage = hasServiceImage && catalogImageLayout === "SQUARE";
  let t4;
  if ($[0] !== animationDelay || $[1] !== className || $[2] !== disabled || $[3] !== footer || $[4] !== hasServiceImage || $[5] !== hideInfoButton || $[6] !== isPreviousAppointment || $[7] !== isRwgLanding || $[8] !== isSelected || $[9] !== item.address || $[10] !== item.caption || $[11] !== item.description || $[12] !== item.id || $[13] !== item.image || $[14] !== item.isPopular || $[15] !== item.isTelehealth || $[16] !== item.name || $[17] !== item.pricing || $[18] !== item.subtitle || $[19] !== item.type || $[20] !== maxQuantity || $[21] !== onInfoClick || $[22] !== onItemClick || $[23] !== onQuantityChange || $[24] !== selectionCount || $[25] !== showQuantityStepper || $[26] !== usesSquareServiceImage) {
    const bookingFee = computeBookingFeeCents(item.pricing);
    const showBookingFee = !isRwgLanding && bookingFee > 0;
    const bookingFeeLabel = item.pricing?.bookingFeeAutoToCredit && bookingFee > 0 ? "deposit" : "booking fee";
    const t5 = item.id;
    const t6 = !hasServiceImage && "pb-12 md:pb-8";
    const t7 = isSelected ? "border-primary bg-primary/10 motion-scale-in-105 hover:md:bg-primary/5" : disabled ? "cursor-not-allowed bg-background-secondary opacity-50" : "border-border/60";
    const t8 = footer && "flex min-h-0 flex-col pb-0 md:pb-0";
    let t9;
    if ($[28] !== className || $[29] !== t6 || $[30] !== t7 || $[31] !== t8) {
      t9 = cx("motion-preset-slide-right-sm relative min-h-32 origin-top cursor-pointer rounded-xl border p-2 px-3 transition-all ease-in-out motion-duration-100/scale focus-within:md:border-primary", t6, "hover:md:scale-105 hover:md:border-primary hover:md:shadow-lg", "hover:md:bg-background", "overflow-hidden", t7, className, t8);
      $[28] = className;
      $[29] = t6;
      $[30] = t7;
      $[31] = t8;
      $[32] = t9;
    } else {
      t9 = $[32];
    }
    const t10 = !isSelected && animationDelay ? `${animationDelay}ms` : undefined;
    let t11;
    if ($[33] !== t10) {
      t11 = {
        animationDelay: t10
      };
      $[33] = t10;
      $[34] = t11;
    } else {
      t11 = $[34];
    }
    let t12;
    if ($[35] !== disabled || $[36] !== item.id || $[37] !== onItemClick) {
      t12 = () => !disabled && onItemClick(item.id);
      $[35] = disabled;
      $[36] = item.id;
      $[37] = onItemClick;
      $[38] = t12;
    } else {
      t12 = $[38];
    }
    let t13;
    if ($[39] !== isPreviousAppointment || $[40] !== item.type) {
      t13 = isPreviousAppointment && <div className={cx("absolute -top-4 right-0 h-fit text-nowrap rounded-full border px-2 py-0.5 md:-right-4", "bg-primary text-caption text-primary-foreground", "motion-preset-slide-right-sm", "motion-delay-700")}>{getTypeMessage(item.type)}</div>;
      $[39] = isPreviousAppointment;
      $[40] = item.type;
      $[41] = t13;
    } else {
      t13 = $[41];
    }
    let t14;
    if ($[42] !== item.id || $[43] !== maxQuantity || $[44] !== onQuantityChange || $[45] !== selectionCount || $[46] !== showQuantityStepper) {
      t14 = showQuantityStepper && onQuantityChange && <div className="absolute bottom-2 left-2 z-10" onClick={_temp}><QuantityStepper value={selectionCount} min={0} max={maxQuantity} onChange={quantity => onQuantityChange(item.id, quantity)} className="shadow-sm" /></div>;
      $[42] = item.id;
      $[43] = maxQuantity;
      $[44] = onQuantityChange;
      $[45] = selectionCount;
      $[46] = showQuantityStepper;
      $[47] = t14;
    } else {
      t14 = $[47];
    }
    let t15;
    if ($[48] !== disabled || $[49] !== item.id || $[50] !== onItemClick) {
      t15 = event_0 => {
        event_0.stopPropagation();
        if (!disabled) {
          onItemClick(item.id);
        }
      };
      $[48] = disabled;
      $[49] = item.id;
      $[50] = onItemClick;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    const t16 = item.type === "provider" ? isSelected : undefined;
    let t17;
    if ($[52] !== item.image || $[53] !== item.name || $[54] !== item.type || $[55] !== usesSquareServiceImage) {
      t17 = typeof item.image === "string" && <img src={item.image} alt={item.name} className={cx("flex-shrink-0 rounded-lg", item.type !== "service" ? "h-12 w-12 object-cover" : usesSquareServiceImage ? "aspect-square h-28 bg-background object-contain" : "aspect-video w-28 object-cover sm:h-28 sm:w-auto")} />;
      $[52] = item.image;
      $[53] = item.name;
      $[54] = item.type;
      $[55] = usesSquareServiceImage;
      $[56] = t17;
    } else {
      t17 = $[56];
    }
    let t18;
    if ($[57] !== item.image) {
      t18 = typeof item.image === "object" && <div className="contents">{item.image}</div>;
      $[57] = item.image;
      $[58] = t18;
    } else {
      t18 = $[58];
    }
    const t19 = hasServiceImage && "pb-12 md:pb-8";
    let t20;
    if ($[59] !== t19) {
      t20 = cx("min-w-0 flex-1", t19);
      $[59] = t19;
      $[60] = t20;
    } else {
      t20 = $[60];
    }
    let t21;
    if ($[61] !== item.name) {
      t21 = <h4 className="line-clamp-2 font-medium">{item.name}</h4>;
      $[61] = item.name;
      $[62] = t21;
    } else {
      t21 = $[62];
    }
    let t22;
    if ($[63] !== item.address) {
      t22 = item.address && <div className="text-body text-muted-foreground">{item.address}</div>;
      $[63] = item.address;
      $[64] = t22;
    } else {
      t22 = $[64];
    }
    let t23;
    if ($[65] !== item.subtitle || $[66] !== item.type) {
      t23 = item.subtitle && <div className={cx(item.type === "provider" ? "text-body-strong text-foreground-secondary" : "text-caption text-muted-foreground")}>{item.subtitle}</div>;
      $[65] = item.subtitle;
      $[66] = item.type;
      $[67] = t23;
    } else {
      t23 = $[67];
    }
    let t24;
    if ($[68] !== item.caption) {
      t24 = item.caption && <p className="text-caption text-foreground-secondary">{item.caption}</p>;
      $[68] = item.caption;
      $[69] = t24;
    } else {
      t24 = $[69];
    }
    let t25;
    if ($[70] !== item.description) {
      t25 = item.description && <p className="mt-1 line-clamp-3 whitespace-pre-wrap text-body text-foreground-secondary">{item.description}</p>;
      $[70] = item.description;
      $[71] = t25;
    } else {
      t25 = $[71];
    }
    let t26;
    if ($[72] !== item.isPopular) {
      t26 = item.isPopular && <PopularBadge />;
      $[72] = item.isPopular;
      $[73] = t26;
    } else {
      t26 = $[73];
    }
    let t27;
    if ($[74] !== item.isTelehealth) {
      t27 = item.isTelehealth && <TelehealthBadge />;
      $[74] = item.isTelehealth;
      $[75] = t27;
    } else {
      t27 = $[75];
    }
    let t28;
    if ($[76] !== item.pricing || $[77] !== item.type) {
      t28 = item.type === "service" && item.pricing?.promotionName && <PromotionBadge promotionName={item.pricing.promotionName} />;
      $[76] = item.pricing;
      $[77] = item.type;
      $[78] = t28;
    } else {
      t28 = $[78];
    }
    let t29;
    if ($[79] !== t26 || $[80] !== t27 || $[81] !== t28) {
      t29 = <div className="mt-1 flex flex-wrap items-center gap-1.5">{t26}{t27}{t28}</div>;
      $[79] = t26;
      $[80] = t27;
      $[81] = t28;
      $[82] = t29;
    } else {
      t29 = $[82];
    }
    let t30;
    if ($[83] !== disabled) {
      t30 = disabled && <p className="mt-1 text-caption text-status-error-text">Not Currently Available</p>;
      $[83] = disabled;
      $[84] = t30;
    } else {
      t30 = $[84];
    }
    let t31;
    if ($[85] !== t20 || $[86] !== t21 || $[87] !== t22 || $[88] !== t23 || $[89] !== t24 || $[90] !== t25 || $[91] !== t29 || $[92] !== t30) {
      t31 = <div className={t20}>{t21}{t22}{t23}{t24}{t25}{t29}{t30}</div>;
      $[85] = t20;
      $[86] = t21;
      $[87] = t22;
      $[88] = t23;
      $[89] = t24;
      $[90] = t25;
      $[91] = t29;
      $[92] = t30;
      $[93] = t31;
    } else {
      t31 = $[93];
    }
    let t32;
    if ($[94] !== disabled || $[95] !== t15 || $[96] !== t16 || $[97] !== t17 || $[98] !== t18 || $[99] !== t31) {
      t32 = <Button type="button" onClick={t15} className="min-w-0 flex-1 items-start justify-between gap-4 !p-0 text-left focus:bg-transparent focus:outline-none hover:md:!bg-transparent" variant="ghost" disabled={disabled} aria-pressed={t16}>{t17}{t18}{t31}</Button>;
      $[94] = disabled;
      $[95] = t15;
      $[96] = t16;
      $[97] = t17;
      $[98] = t18;
      $[99] = t31;
      $[100] = t32;
    } else {
      t32 = $[100];
    }
    let t33;
    if ($[101] !== disabled || $[102] !== hideInfoButton || $[103] !== item.id || $[104] !== item.name || $[105] !== onInfoClick) {
      t33 = !disabled && !hideInfoButton && onInfoClick && <Tooltip content="View details" triggerAsChild={true}><button type="button" aria-label={`View details for ${item.name}`} onPointerDown={_temp2} onMouseDown={_temp3} onClick={e_1 => onInfoClick(e_1, item.id)} className="mt-0.5 flex shrink-0 items-center rounded-full p-0.5 text-primary transition-all duration-200 hover:text-primary/80 active:scale-95"><BsFillQuestionCircleFill className="size-5 transition-transform duration-200 hover:scale-110" /></button></Tooltip>;
      $[101] = disabled;
      $[102] = hideInfoButton;
      $[103] = item.id;
      $[104] = item.name;
      $[105] = onInfoClick;
      $[106] = t33;
    } else {
      t33 = $[106];
    }
    let t34;
    if ($[107] !== t32 || $[108] !== t33) {
      t34 = <div className="flex w-full items-start gap-2">{t32}{t33}</div>;
      $[107] = t32;
      $[108] = t33;
      $[109] = t34;
    } else {
      t34 = $[109];
    }
    t4 = <Card key={t5} data-service-card-id={item.type === "service" ? item.id : undefined} className={t9} style={t11} onClick={t12}>{t13}{t14}{t34}{footer}{item.pricing?.displayPrice && item.pricing.displayPrice > 0 || item.pricing?.originalDisplayPrice ? <div className={cx("absolute bottom-2 right-0 max-h-10", " rounded-s-md bg-gradient-to-l from-background-secondary via-background-secondary to-transparent text-muted-foreground", "flex flex-col items-end justify-center text-right", "px-2 py-1.5 pl-4 text-caption", isSelected && "from-primary/20 via-primary/20 to-transparent")}>{!isRwgLanding && <div className="text-nowrap text-foreground">{showBookingFee ? `${formatCurrency(bookingFee)} ${bookingFeeLabel}` : "$0 booking fee"}</div>}<div><PromotionPriceDisplay pricing={item.pricing} />{!item.pricing?.priceStartsAt && !item.pricing?.estimated && <span> total</span>}</div><div className="pointer-events-none absolute bottom-0 left-4 right-0 h-1 shadow-md" /></div> : showBookingFee ? <div className={cx("absolute right-0", "overflow-hidden rounded-s-md bg-gradient-to-l from-background-secondary via-background-secondary to-transparent", "px-2 py-1 pl-4 text-caption", "bottom-2", isSelected && "from-primary/20 via-primary/20 to-transparent")}><div className="text-right">{formatCurrency(bookingFee)} {bookingFeeLabel}</div><div className="pointer-events-none absolute bottom-0 left-4 right-0 h-1 shadow-md" /></div> : null}</Card>;
    $[0] = animationDelay;
    $[1] = className;
    $[2] = disabled;
    $[3] = footer;
    $[4] = hasServiceImage;
    $[5] = hideInfoButton;
    $[6] = isPreviousAppointment;
    $[7] = isRwgLanding;
    $[8] = isSelected;
    $[9] = item.address;
    $[10] = item.caption;
    $[11] = item.description;
    $[12] = item.id;
    $[13] = item.image;
    $[14] = item.isPopular;
    $[15] = item.isTelehealth;
    $[16] = item.name;
    $[17] = item.pricing;
    $[18] = item.subtitle;
    $[19] = item.type;
    $[20] = maxQuantity;
    $[21] = onInfoClick;
    $[22] = onItemClick;
    $[23] = onQuantityChange;
    $[24] = selectionCount;
    $[25] = showQuantityStepper;
    $[26] = usesSquareServiceImage;
    $[27] = t4;
  } else {
    t4 = $[27];
  }
  return t4;
};
export type ServiceCardProps = Omit<SelfScheduleCardProps, "item" | "onItemClick"> & {
  service: PublicServiceDetail;
  onServiceClick: (serviceId: string) => void;
  /** When false, the duration subtitle is hidden. Defaults to showing it. */
  showDuration?: boolean;
};
export const ServiceCard = t0 => {
  const $ = _c(39);
  let className;
  let onServiceClick;
  let props;
  let service;
  let t1;
  if ($[0] !== t0) {
    ({
      service,
      onServiceClick,
      className,
      showDuration: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onServiceClick;
    $[3] = props;
    $[4] = service;
    $[5] = t1;
  } else {
    className = $[1];
    onServiceClick = $[2];
    props = $[3];
    service = $[4];
    t1 = $[5];
  }
  const showDuration = t1 === undefined ? true : t1;
  let t2;
  if ($[6] !== service) {
    t2 = getServicePricingForSelfSchedule(service);
    $[6] = service;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const pricing = t2;
  let t3;
  if ($[8] !== service || $[9] !== showDuration) {
    t3 = showDuration ? `${patientFacingDurationLabel(service)} minutes` : undefined;
    $[8] = service;
    $[9] = showDuration;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const t4 = service.isPopular ?? false;
  const t5 = service.isVirtual === true;
  const t6 = service.image || undefined;
  let t7;
  if ($[11] !== pricing || $[12] !== service.bookingFee || $[13] !== service.bookingFeeAutoToCredit || $[14] !== service.bookingFeePercentage || $[15] !== service.cancellationCost || $[16] !== service.cancellationFeePctWithin24h || $[17] !== service.cancellationFeePctWithin48h || $[18] !== service.hoursBeforeCancellingCosts || $[19] !== service.noShowFeePercentage || $[20] !== service.price) {
    t7 = {
      ...pricing,
      price: service.price,
      bookingFee: service.bookingFee,
      bookingFeePercentage: service.bookingFeePercentage,
      cancellationFeePctWithin24h: service.cancellationFeePctWithin24h,
      cancellationFeePctWithin48h: service.cancellationFeePctWithin48h,
      noShowFeePercentage: service.noShowFeePercentage,
      cancellationCost: service.cancellationCost,
      hoursBeforeCancellingCosts: service.hoursBeforeCancellingCosts,
      bookingFeeAutoToCredit: service.bookingFeeAutoToCredit
    };
    $[11] = pricing;
    $[12] = service.bookingFee;
    $[13] = service.bookingFeeAutoToCredit;
    $[14] = service.bookingFeePercentage;
    $[15] = service.cancellationCost;
    $[16] = service.cancellationFeePctWithin24h;
    $[17] = service.cancellationFeePctWithin48h;
    $[18] = service.hoursBeforeCancellingCosts;
    $[19] = service.noShowFeePercentage;
    $[20] = service.price;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== service.id || $[23] !== service.name || $[24] !== t3 || $[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = {
      id: service.id,
      name: service.name,
      subtitle: t3,
      type: "service",
      isPopular: t4,
      isTelehealth: t5,
      image: t6,
      pricing: t7
    };
    $[22] = service.id;
    $[23] = service.name;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  const item = t8;
  const t9 = service.allowMultipleBookings ? props.onQuantityChange : undefined;
  const t10 = service.image && "col-span-2";
  let t11;
  if ($[30] !== className || $[31] !== t10) {
    t11 = cx(t10, className);
    $[30] = className;
    $[31] = t10;
    $[32] = t11;
  } else {
    t11 = $[32];
  }
  let t12;
  if ($[33] !== item || $[34] !== onServiceClick || $[35] !== props || $[36] !== t11 || $[37] !== t9) {
    t12 = <SelfScheduleCard item={item} onItemClick={onServiceClick} {...props} onQuantityChange={t9} className={t11} />;
    $[33] = item;
    $[34] = onServiceClick;
    $[35] = props;
    $[36] = t11;
    $[37] = t9;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  return t12;
};
type LocationCardProps = Omit<SelfScheduleCardProps, "item" | "onItemClick"> & {
  location: LocationWithVisibility;
  onLocationClick: (locationId: string) => void;
  notice?: React.ReactNode;
};
export const LocationCard = t0 => {
  const $ = _c(31);
  let location;
  let notice;
  let onLocationClick;
  let props;
  if ($[0] !== t0) {
    ({
      location,
      onLocationClick,
      notice,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = location;
    $[2] = notice;
    $[3] = onLocationClick;
    $[4] = props;
  } else {
    location = $[1];
    notice = $[2];
    onLocationClick = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== location.distance) {
    t1 = location.distance ? formatDistance(location.distance) : undefined;
    $[5] = location.distance;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const subtitle = t1;
  let t2;
  if ($[7] !== location.addressLineOne || $[8] !== location.addressLineTwo || $[9] !== location.city || $[10] !== location.isVirtual || $[11] !== location.state || $[12] !== location.zipCode) {
    t2 = location.isVirtual ? "Telehealth appointment" : formatAddress(location.addressLineOne, location.city, location.state, location.zipCode, location.addressLineTwo);
    $[7] = location.addressLineOne;
    $[8] = location.addressLineTwo;
    $[9] = location.city;
    $[10] = location.isVirtual;
    $[11] = location.state;
    $[12] = location.zipCode;
    $[13] = t2;
  } else {
    t2 = $[13];
  }
  const address = t2;
  let t3;
  if ($[14] !== location.isVirtual || $[15] !== location.name || $[16] !== location.photoUrl) {
    t3 = location.photoUrl ? <img src={location.photoUrl} alt={location.name} className="h-12 w-12 rounded-lg object-cover" /> : location.isVirtual ? <FaPhoneAlt className="h-12 w-12 flex-shrink-0 px-3 text-foreground-muted" /> : <FaMapMarkerAlt className="h-12 w-12 flex-shrink-0 px-3 text-primary/80" />;
    $[14] = location.isVirtual;
    $[15] = location.name;
    $[16] = location.photoUrl;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const image = t3;
  let t4;
  if ($[18] !== address || $[19] !== image || $[20] !== location.id || $[21] !== location.name || $[22] !== subtitle) {
    t4 = {
      id: location.id,
      name: location.name,
      address,
      subtitle,
      type: "location",
      isPopular: false,
      image
    };
    $[18] = address;
    $[19] = image;
    $[20] = location.id;
    $[21] = location.name;
    $[22] = subtitle;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const item = t4;
  let t5;
  if ($[24] !== notice) {
    t5 = notice && <div className="-mx-5 mt-auto overflow-hidden rounded-b-[11px] pt-1">{notice}</div>;
    $[24] = notice;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== item || $[27] !== onLocationClick || $[28] !== props || $[29] !== t5) {
    t6 = <SelfScheduleCard item={item} onItemClick={onLocationClick} {...props} footer={t5} className="overflow-visible p-4 px-5" />;
    $[26] = item;
    $[27] = onLocationClick;
    $[28] = props;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
};
export type ProviderCardProps = Omit<SelfScheduleCardProps, "item" | "onItemClick"> & {
  provider: PublicProviderSummary;
  onProviderClick: (providerId: string) => void;
  /** Small line under the name, such as the provider's next opening. */
  caption?: string;
};
export const ProviderCard = t0 => {
  const $ = _c(20);
  let caption;
  let onProviderClick;
  let props;
  let provider;
  if ($[0] !== t0) {
    ({
      provider,
      onProviderClick,
      caption,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = caption;
    $[2] = onProviderClick;
    $[3] = props;
    $[4] = provider;
  } else {
    caption = $[1];
    onProviderClick = $[2];
    props = $[3];
    provider = $[4];
  }
  let t1;
  if ($[5] !== provider) {
    t1 = formatStaffName(provider);
    $[5] = provider;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const providerFullName = t1;
  const imageAlt = providerFullName;
  let t2;
  if ($[7] !== imageAlt || $[8] !== provider.profileImage) {
    t2 = provider.profileImage ? <Image src={provider.profileImage} alt={imageAlt} width={128} height={128} className="h-12 w-12 flex-shrink-0 rounded-lg object-cover" /> : <FaUser className="h-12 w-12 flex-shrink-0 px-3 text-primary/80" />;
    $[7] = imageAlt;
    $[8] = provider.profileImage;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const image = t2;
  let t3;
  if ($[10] !== caption || $[11] !== image || $[12] !== provider.bio || $[13] !== provider.id || $[14] !== providerFullName) {
    t3 = {
      id: provider.id,
      name: providerFullName,
      caption,
      description: provider.bio,
      type: "provider",
      isPopular: false,
      image
    };
    $[10] = caption;
    $[11] = image;
    $[12] = provider.bio;
    $[13] = provider.id;
    $[14] = providerFullName;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const item = t3;
  let t4;
  if ($[16] !== item || $[17] !== onProviderClick || $[18] !== props) {
    t4 = <SelfScheduleCard item={item} onItemClick={onProviderClick} {...props} className="min-h-0 overflow-visible !pb-4" />;
    $[16] = item;
    $[17] = onProviderClick;
    $[18] = props;
    $[19] = t4;
  } else {
    t4 = $[19];
  }
  return t4;
};
function _temp(event) {
  return event.stopPropagation();
}
function _temp2(e) {
  return e.stopPropagation();
}
function _temp3(e_0) {
  return e_0.stopPropagation();
}